import React, { FC, useEffect } from 'react'

import { Box, Stack, Theme, Typography, useMediaQuery, useTheme } from '@mui/material'
import { isEmpty, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { getJournalingOptionTranslationKey } from '../../TradingLog/tradingLogLabels'
import { fetchPsychologyReport } from '../ReportsSlice'

import { ChartOfCommentsWidget } from './ChartOfCommentsWidget'
import { LevelOfStressWidget } from './LevelOfStressWidget'
import { PositiveNegativeWidget } from './PositiveNegativeWidget'
import { PsychologyPerformanceWidget } from './PsychologyPerformanceWidget'
import { StressDistributionWidget } from './StressDistributionWidget'
import { StressPerformanceWidget } from './StressPerformanceWidget'
import { TraderProfileWidget } from './TraderProfileWidget'

export const PsychologyReports: FC = () => {
  const { spacing } = useTheme()
  const { t } = useTranslation()
  const getJournalingLabel = (name: string) =>
    t(
      `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(name)}`,
      name,
    ) as string
  const matchesBreakpoints: boolean = useMediaQuery((theme: Theme) => theme.breakpoints.up(2000))

  const dispatch = useAppDispatch()

  const { psychology } = useAppSelector(({ reports }) => reports)
  const { period, currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)

  useEffect(() => {
    const periodsIsEqual =
      psychology?.meta?.period && period && isEqual(psychology.meta.period, period)
    const isNeedToUpdate =
      isEmpty(psychology) || !periodsIsEqual || currentAccount?.id !== psychology?.meta.account

    if (currentAccount?.id && isNeedToUpdate) {
      dispatch(
        fetchPsychologyReport({
          account: currentAccount?.id,
          period: period,
        }),
      )
    }
  }, [period, currentAccount])

  return (
    <Stack spacing={2.5} width="100%" minWidth={0}>
      <Box width="100%" minWidth={0}>
        <TraderProfileWidget traderProfile={psychology?.traderProfile} />
      </Box>

      <Stack spacing={0.5}>
        <Typography variant="h5" color="text.secondary">
          {t('psychology', 'Psychology')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'traderProfileSubtitle',
            'A derived overview of discipline, emotionality, and execution risk.',
          )}
        </Typography>
      </Stack>

      <Box
        display="grid"
        gap={2}
        width="100%"
        minWidth={0}
        gridTemplateColumns={{
          xs: '1fr',
          md: matchesBreakpoints ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
        }}
      >
        <Box
          minWidth={0}
          sx={{
            pageBreakInside: 'avoid',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
          }}
        >
          <PositiveNegativeWidget
            moneyWise={psychology?.positiveVsNegative?.moneyWise}
            numberWise={psychology?.positiveVsNegative?.numberWise}
          />
        </Box>
        <Box
          minWidth={0}
          sx={{
            pageBreakInside: 'avoid',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
          }}
        >
          <ChartOfCommentsWidget
            positive={psychology?.chartOfComments?.positive}
            negative={psychology?.chartOfComments?.negative}
          />
        </Box>
        <Box
          minWidth={0}
          sx={{
            pageBreakInside: 'avoid',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
          }}
        >
          <StressPerformanceWidget
            positive={psychology?.stressPerformance?.positive}
            negative={psychology?.stressPerformance?.negative}
          />
        </Box>
        <Box
          minWidth={0}
          sx={{
            pageBreakInside: 'avoid',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
          }}
        >
          <LevelOfStressWidget levelData={psychology?.topInstrumentByLevelOfStress} />
        </Box>
        <Box
          minWidth={0}
          sx={{
            pageBreakInside: 'avoid',
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
          }}
        >
          <StressDistributionWidget stressDistribution={psychology?.stressDistribution} />
        </Box>
      </Box>

      <Box
        display="grid"
        gap={2}
        width="100%"
        minWidth={0}
        alignItems="start"
        gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
      >
        <Box minWidth={0} sx={{ display: 'flex', alignSelf: 'stretch' }}>
          <PsychologyPerformanceWidget
            title={t('psychologyPage.widgets.emotionPerformance.title', 'Emotion vs P&L')}
            subtitle={t(
              'psychologyPage.widgets.emotionPerformance.subtitle',
              'Negative shows risk-associated tags. Positive also includes context-dependent emotions, which are labelled separately. All rows are visible; Strongest and Weakest signals require at least 3 trades.',
            )}
            explanation={
              t(
                'psychologyPage.widgets.emotionPerformance.explanation',
                'Shows which emotion tags are most associated with strong or weak outcomes.',
              ) as string
            }
            emotionFilter
            fillHeight
            rows={
              psychology?.emotionPerformance?.map((item) => ({
                title: getJournalingLabel(item.tag),
                isNegative: item.isNegative,
                emotionGroup: item.emotionGroup,
                trades: item.trades,
                caption: t(
                  'psychologyPage.widgets.shared.tradesWinRate',
                  '{{trades}} trades • {{winRate}}% win rate',
                  {
                    trades: item.trades,
                    winRate: item.winRate,
                  },
                ),
                rate: item.winRate,
                rateLabel: t('winRate', 'Win rate') as string,
                avgPnl: item.avgPnl,
                totalPnl: item.pnl,
              })) || []
            }
          />
        </Box>
        <Box minWidth={0} sx={{ display: 'flex', alignSelf: 'stretch' }}>
          <PsychologyPerformanceWidget
            title={t('psychologyPage.widgets.ruleBreaks.title', 'Rule breaks')}
            subtitle={t(
              'psychologyPage.widgets.ruleBreaks.subtitle',
              'All rule breaks are visible; Strongest and Weakest signals require at least 3 trades.',
            )}
            explanation={
              t(
                'psychologyPage.widgets.ruleBreaks.explanation',
                'Highlights which rule violations correlate with the weakest performance.',
              ) as string
            }
            fillHeight
            rows={
              psychology?.ruleBreakdown?.map((item) => ({
                title: getJournalingLabel(item.rule),
                trades: item.trades,
                caption: t(
                  'psychologyPage.widgets.shared.tradesLossRate',
                  '{{trades}} trades • {{lossRate}}% loss rate',
                  {
                    trades: item.trades,
                    lossRate: item.lossRate,
                  },
                ),
                rate: item.lossRate,
                rateLabel: t('lossRate', 'Loss rate') as string,
                avgPnl: item.avgPnl,
                totalPnl: item.pnl,
              })) || []
            }
          />
        </Box>
        <Box minWidth={0} sx={{ display: 'flex', alignSelf: 'flex-start' }}>
          <PsychologyPerformanceWidget
            title={t('psychologyPage.widgets.planAdherence.title', 'Plan adherence')}
            subtitle={t(
              'psychologyPage.widgets.planAdherence.subtitle',
              'Performance split by whether the trader followed the plan.',
            )}
            explanation={
              t(
                'psychologyPage.widgets.planAdherence.explanation',
                'Compares outcomes when the plan was followed versus ignored.',
              ) as string
            }
            showSummary={false}
            rows={[
              {
                title: t('psychologyPage.widgets.planAdherence.followedPlan', 'Followed plan'),
                trades: psychology?.planAdherencePerformance?.followedPlan?.trades || 0,
                caption: t(
                  'psychologyPage.widgets.shared.tradesWinRate',
                  '{{trades}} trades • {{winRate}}% win rate',
                  {
                    trades: psychology?.planAdherencePerformance?.followedPlan?.trades || 0,
                    winRate: psychology?.planAdherencePerformance?.followedPlan?.winRate || 0,
                  },
                ),
                rate: psychology?.planAdherencePerformance?.followedPlan?.winRate || 0,
                rateLabel: t('winRate', 'Win rate') as string,
                avgPnl: psychology?.planAdherencePerformance?.followedPlan?.avgPnl || 0,
                totalPnl: psychology?.planAdherencePerformance?.followedPlan?.pnl || 0,
              },
              {
                title: t(
                  'psychologyPage.widgets.planAdherence.didNotFollowPlan',
                  'Did not follow plan',
                ),
                trades: psychology?.planAdherencePerformance?.didNotFollowPlan?.trades || 0,
                caption: t(
                  'psychologyPage.widgets.shared.tradesWinRate',
                  '{{trades}} trades • {{winRate}}% win rate',
                  {
                    trades: psychology?.planAdherencePerformance?.didNotFollowPlan?.trades || 0,
                    winRate: psychology?.planAdherencePerformance?.didNotFollowPlan?.winRate || 0,
                  },
                ),
                rate: psychology?.planAdherencePerformance?.didNotFollowPlan?.winRate || 0,
                rateLabel: t('winRate', 'Win rate') as string,
                avgPnl: psychology?.planAdherencePerformance?.didNotFollowPlan?.avgPnl || 0,
                totalPnl: psychology?.planAdherencePerformance?.didNotFollowPlan?.pnl || 0,
              },
            ]}
          />
        </Box>
        {psychology?.aiJournalAnalysisEnabled && (
          <Box minWidth={0} sx={{ display: 'flex', alignSelf: 'flex-start' }}>
            <PsychologyPerformanceWidget
              title={t(
                'psychologyPage.widgets.sentimentCorrelation.title',
                'Sentiment correlation',
              )}
              subtitle={t(
                'psychologyPage.widgets.sentimentCorrelation.subtitle',
                'Free-text note sentiment and discipline scores correlated with outcomes.',
              )}
              explanation={
                t(
                  'psychologyPage.widgets.sentimentCorrelation.explanation',
                  'Compares note-sentiment buckets with win rate, discipline signal, and average P&L.',
                ) as string
              }
              rows={
                psychology?.sentimentCorrelation?.map((item) => ({
                  title: t(`psychologyPage.sentimentBuckets.${item.bucket}`, item.bucket),
                  trades: item.trades,
                  caption: t(
                    'psychologyPage.widgets.sentimentCorrelation.rowSubtitle',
                    '{{trades}} trades • {{winRate}}% win rate • discipline {{disciplineScore}}',
                    {
                      trades: item.trades,
                      winRate: item.winRate,
                      disciplineScore: item.disciplineScore,
                    },
                  ),
                  rate: item.winRate,
                  rateLabel: t('winRate', 'Win rate') as string,
                  avgPnl: item.avgPnl,
                  extraLabel: t('disciplineScore', 'Discipline score') as string,
                  extraValue: `${item.disciplineScore}`,
                })) || []
              }
            />
          </Box>
        )}
      </Box>
    </Stack>
  )
}
